import { BrowserProvider } from 'ethers';
import api from './api';

export function truncateAddress(addr) {
  const s = String(addr || '');
  if (s.length < 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

async function getEip1193Provider() {
  if (typeof window !== 'undefined' && window.ethereum) return window.ethereum;
  throw new Error('No wallet provider found. Connect your wallet first.');
}

function emitWalletLimit(data, attempted) {
  try {
    window.dispatchEvent(
      new CustomEvent('app:wallet-limit', {
        detail: {
          max: data?.max,
          wallets: data?.wallets || [],
          attempted,
          message: data?.message,
        },
      })
    );
  } catch {
    /* ignore */
  }
}

/**
 * Ask the connected wallet to sign a one-time ownership message from the backend.
 * @param {string} address
 * @param {'auth'|'link'} purpose
 */
export async function proveWalletOwnership(address, purpose = 'auth') {
  if (!address) throw new Error('Wallet address is required');
  const { data: challenge } = await api.post('/auth/wallet-challenge', {
    address,
    purpose: purpose === 'link' ? 'link' : 'auth',
  });
  if (!challenge?.message || !challenge?.nonce) {
    throw new Error('Failed to create wallet ownership challenge');
  }

  const eip1193 = await getEip1193Provider();
  const provider = new BrowserProvider(eip1193);
  const signer = await provider.getSigner();
  const signerAddr = String(await signer.getAddress()).toLowerCase();
  if (signerAddr !== String(address).toLowerCase()) {
    throw new Error('Connected wallet does not match the address being verified');
  }

  const signature = await signer.signMessage(challenge.message);
  return {
    address,
    signature,
    nonce: challenge.nonce,
    message: challenge.message,
  };
}

/** Login or signup with signed ownership proof. */
export async function walletAuthWithProof(address, { referralCode } = {}) {
  const proof = await proveWalletOwnership(address, 'auth');
  try {
    return await api.post('/auth/wallet-login', {
      address: proof.address,
      signature: proof.signature,
      nonce: proof.nonce,
    });
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
    return api.post('/auth/wallet-signup', {
      address: proof.address,
      signature: proof.signature,
      nonce: proof.nonce,
      referralCode: referralCode || undefined,
    });
  }
}

/**
 * Link wallet with signed proof.
 * Already-linked wallets succeed without a signature.
 * On WALLET_LIMIT_REACHED, dispatches `app:wallet-limit` for the modal host.
 */
export async function linkWalletWithProof(address) {
  try {
    const probe = await api.post('/auth/wallets/link', { address });
    if (probe.data?.alreadyLinked) return probe;
    // Unexpected success without proof — accept
    if (probe.data?.ok) return probe;
  } catch (err) {
    const data = err?.response?.data;
    const code = data?.code;
    if (code === 'WALLET_LIMIT_REACHED') {
      emitWalletLimit(data, address);
      throw err;
    }
    if (code === 'WALLET_IN_USE' || err?.response?.status === 409) {
      throw err;
    }
    // Need signature (proof required) — continue
  }

  const proof = await proveWalletOwnership(address, 'link');
  try {
    return await api.post('/auth/wallets/link', {
      address: proof.address,
      signature: proof.signature,
      nonce: proof.nonce,
    });
  } catch (err) {
    const data = err?.response?.data;
    if (data?.code === 'WALLET_LIMIT_REACHED') {
      emitWalletLimit(data, address);
    }
    throw err;
  }
}

export function isWalletLimitError(err) {
  return err?.response?.data?.code === 'WALLET_LIMIT_REACHED';
}
